// In this file, "user" means "current user".
// In this file, "currentUser" means "current user".

import { filterByPropertyValues, isPropertyValueExists, getObjectByPropertyValue } from 'utilities/collection';

import * as roleNames from 'constants/role-names';
import * as startupPermissions from 'constants/permissions/startup';
import * as helptapPermissions from 'constants/permissions/helptap';

class AccessController {

  constructor(application) {

    this.application = application;
    this.startupRoleNames = {
      CUSTOMER: '',
      MEMBER: '',
      ADMINISTRATOR: ''
    };
    this.startupRoles = [];
    this.userRoles = [];
    this.userPermissions = [];

  }

  clear() {

    this.userRoles = [];
    this.userPermissions = [];

  }

  async initialize(bootstrapData = {}) {

    this.startupRoles = bootstrapData.roles;
    this.userPermissions = bootstrapData.permissions;

    this.computeStartupRoleNames();
    this.computeUserRoles();

  }

  getRoles() {

    function toEyeFriendly(role) {

      role.name = role.name.split(':')[1];

      return { ...role };

    }

    return structuredClone(this.startupRoles).map(toEyeFriendly);

  }

  async loadStartupRoles() {

    const startupRoles = await this.application.serverInterface.startup.getRoles();

    this.startupRoles = startupRoles;

  }

  async loadUserPermissions() {

    const userPermissions = await this.application.serverInterface.currentUser.getPermissionNamesByStartupUserId();

    this.userPermissions = userPermissions;

  }

  computeStartupRoleNames() {

    const startup = this.application.getCurrentStartup();
    const startupId = startup._id;

    this.startupRoleNames = {
      CUSTOMER: `${startupId}:${roleNames.CUSTOMER}`,
      MEMBER: `${startupId}:${roleNames.MEMBER}`,
      ADMINISTRATOR: `${startupId}:${roleNames.ADMINISTRATOR}`
    };

  }

  computeUserRoles() {

    const user = this.application.getUser();

    const userRoles = filterByPropertyValues(this.startupRoles, '_id', user.roleIds);

    this.userRoles = userRoles;

  }

  isOwnerOfStartup() {

    return this.application.getUser()._id === this.application.startup.ownerId;

  }

  isAllowedToInviteCustomers() {

    return this.isMemberOrAdministrator();

  }

  isAllowedToViewCustomersSection() {

    if (this.application.isAuthenticated() === false) {
      return false;
    }

    return this.isMemberOrAdministrator();

  }

  isAllowedToViewPagesSection() {

    if (this.application.isAuthenticated() === false) {
      return false;
    }

    return this.isMemberOrAdministrator();

  }

  isAllowedToEditCustomerProfile() {

    return this.isMemberOrAdministrator();

  }

  isOwnerOfPost(postOwnerId) {

    if (this.application.isAuthenticated() === false) {
      return false;
    }

    return this.application.getUser()._id === postOwnerId;

  }

  hasPermission(permissionName) {

    return this.userPermissions.indexOf(permissionName) !== -1;

  }

  hasStartupPermission(permissionName) {

    const _permissionName = `${this.application.startup._id}:${permissionName}`;

    return this.userPermissions.indexOf(_permissionName) !== -1;

  }

  getCustomerRole(startupRoles) {

    const _startupRoles = startupRoles || this.startupRoles;

    const roleName = this.startupRoleNames.CUSTOMER;

    return getObjectByPropertyValue(_startupRoles, 'name', roleName);

  }

  isCustomer(userRoles) {

    const _userRoles = userRoles || this.userRoles;

    const roleName = this.startupRoleNames.CUSTOMER;

    const customerRoleExists = isPropertyValueExists(_userRoles, 'name', roleName);

    return customerRoleExists && this.userRoles.length === 1;

  }

  isMember(userRoles) {

    const _userRoles = userRoles || this.userRoles;

    const roleName = this.startupRoleNames.MEMBER;

    const memberRoleExists = isPropertyValueExists(_userRoles, 'name', roleName);

    return memberRoleExists && this.isAdministrator(userRoles) === false;

  }

  isAdministrator(userRoles) {

    const _userRoles = userRoles || this.userRoles;

    const roleName = this.startupRoleNames.ADMINISTRATOR;

    const administratorRoleExists = isPropertyValueExists(_userRoles, 'name', roleName);

    return administratorRoleExists;

  }

  getHighestRoleOfUser(user) {

    const roleIds = user.roleIds || [];
    const userRoles = filterByPropertyValues(this.startupRoles, '_id', roleIds);

    if (this.isAdministrator(userRoles) === true) {
      return roleNames.ADMINISTRATOR;
    }

    if (this.isMember(userRoles) === true) {
      return roleNames.MEMBER;
    }

    if (user.email === null) {
      return 'Visitor';
    }

    return roleNames.CUSTOMER.replace('Startup ', '');

  }

  canEditStartupSettings() {

    return this.hasStartupPermission(startupPermissions.UPDATE_STARTUP_DETAILS_AND_SETTINGS);

  }

  canCreateStartup() {

    return this.hasPermission(helptapPermissions.CREATE_STARTUP_DRAFT);

  }

  canEditPost(post) {

    if (this.application.isAuthenticated() === false) {
      return false;
    }

    if (this.isAdministrator() === true) {
      return true;
    }

    if (this.isMember() === true) {
      return true;
    }

    return post.ownerId === this.application.getUser()._id;

  }

  canDeleteTextReaction(textReaction) {

    if (this.application.isAuthenticated() === false) {
      return false;
    }

    if (this.isAdministrator() === true) {
      return true;
    }

    if (this.isMember() === true) {
      return true;
    }

    return this.application.getUser()._id === textReaction.ownerId;;

  }

  canEditTextReaction(textReaction) {

    if (this.application.isAuthenticated() === false) {
      return false;
    }

    return this.application.getUser()._id === textReaction.ownerId;

  }

  isMemberOrAdministrator() {

    if (this.isAdministrator() === true) {
      return true;
    }

    return this.isMember();

  }

  canCreateLiveStreaming() {

    if (this.isCustomer() === true) {
      return false;
    }

    return this.application.getCurrentStartup().liveStreamingMinutes.available > 0;

  }

  canAddEvent() {

    return this.canCreateLiveStreaming();

  }

  isMemberUser(user) {

    const userRoles = filterByPropertyValues(this.startupRoles, '_id', user.roleIds);

    return this.isMember(userRoles);

  }

  isAdministratorUser(user) {

    const userRoles = filterByPropertyValues(this.startupRoles, '_id', user.roleIds);

    return this.isAdministrator(userRoles);

  }

}

export default AccessController;